import { useEffect, useState, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  Box,
  CircularProgress,
  Dialog,
  DialogContent,
  Typography,
} from "@material-ui/core";
import {
  CheckCircleOutline as CheckIcon,
  ErrorOutline as ErrorIcon,
} from "@material-ui/icons";
import AccessRights from "../../../../configs/constants/accessRights";
import getCompanyDropdown from "../../../../configs/apis/consumer-engagement/getCompanyDropdown.api";
import getCompanyUsers from "../../../../configs/apis/companies/getCompanyUsers.api";
import { adminCreateTicket } from "../../redux/action";
import CreateTicketPage from "./createTicket.page";

export default function CreateTicketContainer() {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const canCreate = useSelector((state) =>
    state.session.accessRights.includes(AccessRights.SUPPORT_TICKET_ACCESS)
    && state.session.accessRights.includes(AccessRights.SUPPORT_TICKET_CREATE)
  );

  useEffect(() => {
    if (!canCreate) {
      navigate("/admin/support");
    }
  }, [canCreate]);

  const [companies, setCompanies] = useState([]);
  const [companySearch, setCompanySearch] = useState("");
  const [companiesLoading, setCompaniesLoading] = useState(false);

  const [users, setUsers] = useState([]);
  const [userSearch, setUserSearch] = useState("");
  const [usersLoading, setUsersLoading] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState(null);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [loadingDialog, setLoadingDialog] = useState({
    open: false,
    status: "loading",
    message: "",
  });

  // Fetch companies
  const fetchCompanies = useCallback(async (search = "") => {
    setCompaniesLoading(true);
    try {
      const result = await getCompanyDropdown({ start: 0, length: 50, search });
      setCompanies(result.data || result || []);
    } catch (e) {
      setCompanies([]);
    }
    setCompaniesLoading(false);
  }, []);

  useEffect(() => {
    fetchCompanies();
  }, [fetchCompanies]);

  useEffect(() => {
    const timer = setTimeout(() => {
      fetchCompanies(companySearch);
    }, 300);
    return () => clearTimeout(timer);
  }, [companySearch, fetchCompanies]);

  // Fetch users when company is selected
  const fetchUsers = useCallback(async (companyId, search = "") => {
    if (!companyId) {
      setUsers([]);
      return;
    }
    setUsersLoading(true);
    try {
      const result = await getCompanyUsers({
        companyId,
        page: 0,
        length: 50,
        query: search,
        searchColumn: "name",
        status: "active",
      });
      setUsers(result.data?.data || result.data || []);
    } catch (e) {
      setUsers([]);
    }
    setUsersLoading(false);
  }, []);

  useEffect(() => {
    if (selectedCompany) {
      fetchUsers(selectedCompany.id || selectedCompany.uuid);
    } else {
      setUsers([]);
    }
  }, [selectedCompany, fetchUsers]);

  useEffect(() => {
    if (selectedCompany) {
      const timer = setTimeout(() => {
        fetchUsers(selectedCompany.id || selectedCompany.uuid, userSearch);
      }, 300);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [userSearch, selectedCompany, fetchUsers]);

  const handleCompanyChange = (company) => {
    setSelectedCompany(company);
    setUsers([]);
    setUserSearch("");
  };

  const handleSubmit = async (values) => {
    setIsSubmitting(true);
    setLoadingDialog({
      open: true,
      status: "loading",
      message: values.attachments?.length
        ? "Uploading files and creating ticket..."
        : "Creating ticket...",
    });

    const ticketData = {
      company_id: values.company_id,
      reporter_id: values.reporter_id,
      reporter_name: values.reporter_name,
      reporter_email: values.reporter_email,
      reporter_company_name: values.reporter_company_name,
      title: values.title,
      description: values.description,
      smartkood_module: values.smartkood_module,
      files: values.attachments || [],
    };

    const result = await dispatch(adminCreateTicket(ticketData));

    if (adminCreateTicket.fulfilled.match(result)) {
      const ticketId = result.payload?.data?.id;

      setLoadingDialog({
        open: true,
        status: "success",
        message: "Ticket created successfully!",
      });

      setTimeout(() => {
        setLoadingDialog({ open: false, status: "loading", message: "" });
        setIsSubmitting(false);
        if (ticketId) {
          navigate(`/admin/support/tickets/${ticketId}`);
        } else {
          navigate("/admin/support");
        }
      }, 1200);
    } else {
      setLoadingDialog({
        open: true,
        status: "error",
        message: result.payload || "Failed to create ticket. Please try again.",
      });

      setTimeout(() => {
        setLoadingDialog({ open: false, status: "loading", message: "" });
        setIsSubmitting(false);
      }, 3000);
    }
  };

  const handleCancel = () => {
    navigate("/admin/support");
  };

  return (
    <>
      <CreateTicketPage
        companies={companies}
        companiesLoading={companiesLoading}
        companySearch={companySearch}
        onCompanySearchChange={setCompanySearch}
        onCompanyChange={handleCompanyChange}
        selectedCompany={selectedCompany}
        users={users}
        usersLoading={usersLoading}
        userSearch={userSearch}
        onUserSearchChange={setUserSearch}
        isSubmitting={isSubmitting}
        onSubmit={handleSubmit}
        onCancel={handleCancel}
      />

      <Dialog
        open={loadingDialog.open}
        PaperProps={{
          sx: {
            borderRadius: "16px",
            padding: "16px 8px",
            minWidth: 320,
            textAlign: "center",
          },
        }}
      >
        <DialogContent>
          <Box display="flex" flexDirection="column" alignItems="center" py={2}>
            {loadingDialog.status === "loading" && (
              <CircularProgress size={48} sx={{ color: "#5A9559", mb: 2 }} />
            )}
            {loadingDialog.status === "success" && (
              <CheckIcon sx={{ fontSize: 48, color: "#5A9559", mb: 2 }} />
            )}
            {loadingDialog.status === "error" && (
              <ErrorIcon sx={{ fontSize: 48, color: "#DC2626", mb: 2 }} />
            )}
            <Typography
              sx={{
                fontSize: 15,
                fontWeight: 500,
                color: loadingDialog.status === "error" ? "#DC2626" : "#344054",
              }}
            >
              {loadingDialog.message}
            </Typography>
          </Box>
        </DialogContent>
      </Dialog>
    </>
  );
}
